import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Landmark, Printer, ClipboardList, Globe, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { api, errMsg } from "@/lib/api";

const OPTIONS = [
  ["manager", ClipboardList, "fiscal_opt_manager", "fiscal_opt_manager_desc"],
  ["printer", Printer, "fiscal_opt_printer", "fiscal_opt_printer_desc"],
  ["acube", Globe, "fiscal_opt_acube", "fiscal_opt_acube_desc"],
];

export function FiscalBanner() {
  const { restaurant, user, reloadRestaurants } = useAuth();
  const { t } = useT();
  const nav = useNavigate();
  const [busy, setBusy] = useState("");
  const [hidden, setHidden] = useState(() => sessionStorage.getItem(`fiscal-banner-${restaurant?.id}`) === "1");
  if (!restaurant || user.role !== "owner") return null;
  const needsData = restaurant.fiscal_mode === "acube" && !restaurant.fiscal_complete;
  if ((restaurant.fiscal_mode && !needsData) || hidden) return null;

  const choose = async (mode) => {
    setBusy(mode);
    try {
      await api.put(`/restaurants/${restaurant.id}/fiscal-mode`, { mode });
      await reloadRestaurants();
      toast.success(t("saved"));
      if (mode === "acube") nav("/app/settings");
    } catch (ex) { toast.error(errMsg(ex)); } finally { setBusy(""); }
  };
  const dismiss = () => { sessionStorage.setItem(`fiscal-banner-${restaurant.id}`, "1"); setHidden(true); };

  return (
    <div data-testid="fiscal-banner" className="relative mb-6 overflow-hidden rounded-3xl border border-[#5aa469]/30 bg-[#f6faf6] p-5 shadow-sm">
      <button data-testid="fiscal-banner-dismiss" onClick={dismiss} aria-label="close" className="absolute right-3 top-3 rounded-full p-1 text-slate-400 hover:bg-white hover:text-slate-700"><X size={16} /></button>
      <div className="flex items-start gap-3">
        <span className="rounded-2xl bg-[#5aa469]/15 p-2.5 text-[#4a8a56]"><Landmark size={22} /></span>
        <div className="min-w-0">
          <p className="font-heading text-base font-extrabold text-slate-900">{needsData ? t("fiscal_banner_acube_title") : t("fiscal_banner_title")}</p>
          <p className="mt-0.5 text-sm text-slate-600">{needsData ? t("fiscal_banner_acube_desc") : t("fiscal_banner_desc")}</p>
        </div>
      </div>
      {needsData ? (
        <div className="mt-4 flex flex-wrap gap-2">
          <button data-testid="fiscal-banner-complete-btn" onClick={() => nav("/app/settings")} className="rounded-full bg-[#5aa469] px-5 py-2 text-sm font-semibold text-white hover:bg-[#4a8a56]">{t("fiscal_banner_complete")}</button>
          <button data-testid="fiscal-banner-change-btn" onClick={() => choose("manager")} className="rounded-full border border-slate-200 bg-white px-5 py-2 text-sm font-semibold text-slate-700 hover:border-slate-400">{t("fiscal_banner_change")}</button>
        </div>
      ) : (
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          {OPTIONS.map(([mode, Icon, title, desc]) => (
            <button key={mode} data-testid={`fiscal-opt-${mode}`} disabled={!!busy} onClick={() => choose(mode)}
              className="group flex flex-col items-start rounded-2xl border border-slate-200 bg-white p-4 text-left transition-colors hover:border-[#5aa469] disabled:opacity-60">
              <Icon size={20} className="text-[#4a8a56]" />
              <span className="mt-2 text-sm font-bold text-slate-900">{t(title)}</span>
              <span className="mt-1 text-xs text-slate-500">{t(desc)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
